import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useUserDataContext } from '../../../contexts/UserDataContext.jsx';
import GoalModal from './GoalModal'; 

const GoalProgressWidget = ({ goals, className = "" }) => {
  const { updateUserData, userData } = useUserDataContext();
  const [isModalOpen, setModalOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);

  const handleAddGoal = () => {
    setEditingGoal(null);
    setModalOpen(true);
  };

  const handleEditGoal = (goal) => {
    setEditingGoal(goal);
    setModalOpen(true);
  };

  const handleSaveGoal = async (goal) => {
    const newGoals = [...(userData.goals || [])];
    if (goal.id) {
      const index = newGoals.findIndex(g => g.id === goal.id);
      newGoals[index] = goal;
    } else {
      newGoals.push({ ...goal, id: Date.now() });
    }
    await updateUserData({ goals: newGoals });
  };

  const handleDeleteGoal = async (goalId) => {
    const newGoals = userData.goals.filter(goal => goal.id !== goalId);
    await updateUserData({ goals: newGoals });
  };

  const getGoalStatus = (goal) => {
    if (!goal || typeof goal.progress !== 'number' || !goal.deadline || !goal.id) {
      return 'not-set'; 
    }
  
    if (goal.progress >= 100) {
      return 'completed';
    }
  
    const today = new Date();
    const deadlineDate = new Date(goal.deadline);
    // Assuming goal.id is the creation timestamp which acts as the start date
    const startDate = new Date(goal.id);
  
    if (today > deadlineDate) {
      return 'behind'; // Deadline has passed and goal is not complete
    }

    if (startDate >= deadlineDate) {
      return 'behind'; // Invalid dates
    }
  
    const totalDuration = deadlineDate.getTime() - startDate.getTime();
    const elapsedDuration = today.getTime() - startDate.getTime();

    if (elapsedDuration < 0) {
        return 'not-set'; // Goal starts in the future
    }
  
    const expectedProgress = (elapsedDuration / totalDuration) * 100;
  
    if (goal.progress >= expectedProgress) {
      return 'on-track';
    } else if (goal.progress >= expectedProgress - 25) { // Within a 25% tolerance
      return 'at-risk';
    } else {
      return 'behind';
    }
  };

  const getProgressColor = (progress) => {
    if (progress >= 90) return 'bg-success';
    if (progress >= 70) return 'bg-accent';
    if (progress >= 50) return 'bg-warning';
    return 'bg-destructive/60';
  };
  
  const getProgressTextColor = (progress) => {
    if (progress >= 90) return 'text-success';
    if (progress >= 70) return 'text-accent';
    if (progress >= 50) return 'text-warning';
    return 'text-destructive';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'CheckCircle2';
      case 'on-track': return 'TrendingUp';
      case 'behind': return 'AlertTriangle';
      case 'at-risk': return 'AlertCircle';
      default: return 'Circle';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success';
      case 'on-track': return 'text-accent';
      case 'behind': return 'text-warning';
      case 'at-risk': return 'text-destructive';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <>
      <div className={`bg-card border border-border rounded-clinical p-4 sm:p-6 shadow-clinical ${className}`}>
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h3 className="font-heading font-heading-semibold text-lg text-card-foreground">
            Goal Progress
          </h3>
          <Button variant="outline" size="sm" iconName="Plus" iconSize={16} onClick={handleAddGoal}>
            Add Goal
          </Button>
        </div>
        <div className="space-y-4 sm:space-y-6">
          {goals?.map((goal) => {
            const status = getGoalStatus(goal);
            return (
              <div key={goal?.id} className="space-y-3 cursor-pointer" onClick={() => handleEditGoal(goal)}>
                {/* Goal Header */}
                <div className="flex flex-col sm:flex-row items-start justify-between">
                  <div className="flex-1 mb-2 sm:mb-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <Icon 
                        name={getStatusIcon(status)} 
                        size={16} 
                        className={getStatusColor(status)} 
                      />
                      <h4 className="font-body font-body-medium text-sm text-card-foreground">
                        {goal?.title}
                      </h4>
                    </div>
                    <p className="font-caption font-caption-normal text-xs text-muted-foreground">
                      Target: {goal?.target} | Deadline: {goal?.deadline}
                    </p>
                  </div>
                  <div className="text-left sm:text-right">
                    <div className={`font-data font-data-normal text-sm ${getProgressTextColor(goal?.progress)}`}>
                      {goal?.progress}%
                    </div>
                    <div className="font-caption font-caption-normal text-xs text-muted-foreground capitalize">
                      {status.replace('-', ' ')}
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-clinical-slow ${getProgressColor(goal?.progress)}`}
                    style={{ width: `${goal?.progress}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
        {/* Summary Stats */}
        <div className="mt-6 pt-6 border-t border-border">
          <div className="flex flex-wrap items-center justify-around gap-4 text-center">
            <div>
              <div className="font-data font-data-normal text-lg text-success">
                {goals?.filter(g => getGoalStatus(g) === 'completed')?.length}
              </div>
              <div className="font-caption font-caption-normal text-xs text-muted-foreground">
                Completed
              </div>
            </div>
            <div>
              <div className="font-data font-data-normal text-lg text-accent">
                {goals?.filter(g => getGoalStatus(g) === 'on-track')?.length}
              </div>
              <div className="font-caption font-caption-normal text-xs text-muted-foreground">
                On Track
              </div>
            </div>
            <div>
              <div className="font-data font-data-normal text-lg text-warning">
                {goals?.filter(g => ['behind', 'at-risk']?.includes(getGoalStatus(g)))?.length}
              </div>
              <div className="font-caption font-caption-normal text-xs text-muted-foreground">
                Need Attention
              </div>
            </div>
          </div>
        </div>
      </div>
      <GoalModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSaveGoal}
        onDelete={handleDeleteGoal}
        goal={editingGoal}
      />
    </>
  );
};

export default GoalProgressWidget;
